import { ADMIN_API_CORE_KEY, API_CORE_URL, API_CORE_CONFIG } from './config';
import { getAuthHeaders, redirectToLogin } from './auth';

export type QueryParams = Record<string, unknown>;

export interface RequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  requireAuth?: boolean;
  /**
   * Si es true, no adjunta el token Bearer de sesión (getAuthHeaders) ni redirige a
   * login en 401/403. Usar en llamadas desde páginas públicas/no autenticadas (ej.
   * CertActionPage, abierta desde un link de email) que dependen únicamente de la
   * x-api-key: si el navegador tiene guardado un token de una sesión de psicóloga
   * (propia o de otra prueba), el backend puede resolver la identidad a partir de
   * ese Bearer en vez de usar los parámetros explícitos de la llamada (ej. el
   * destinatario "to" de un email), o puede redirigir a login si el token expiró.
   *
   * OJO: sólo sirve contra endpoints que autentican por x-api-key (ej.
   * `/v1/psychologists/*`). Contra `/v1/psi-platform/*` NO funciona: ese prefijo pasa por
   * `PsiAuthTokenMiddleware`, que exige Bearer y responde 401. Ese fue exactamente el bug
   * de `notifyTeam` y del rechazo de certificados, que quedaron caídos desde 2026-07-06.
   */
  skipSessionAuth?: boolean;
}

function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) usp.set(key, String(value));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

/**
 * Crear AbortController con timeout
 */
function createTimeoutController(timeoutMs: number): AbortController {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller;
}

/**
 * Función de reintento con delay exponencial
 */
async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Request unificado con manejo automático de autenticación, reintentos y timeouts
 */
async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const {
    timeout = API_CORE_CONFIG.TIMEOUT,
    retries = API_CORE_CONFIG.MAX_RETRIES,
    requireAuth = false,
    skipSessionAuth = false,
    ...init
  } = options;

  const url = `${API_CORE_URL}${path}`;

  // Configurar headers
  const headers = new Headers(init?.headers || {});

  // Content-Type por defecto
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // API Key si está disponible
  if (ADMIN_API_CORE_KEY) {
    headers.set('x-api-key', ADMIN_API_CORE_KEY);
  }

  // Headers de autorización automáticos (omitidos si skipSessionAuth)
  if (!skipSessionAuth) {
    const authHeaders = getAuthHeaders();
    Object.entries(authHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
  }

  // Verificar autenticación si es requerida
  if (requireAuth && !headers.has('Authorization')) {
    throw new Error('Se requiere autenticación para acceder a este recurso');
  }

  // Función de request con reintentos
  const makeRequest = async (attempt: number): Promise<Response> => {
    const controller = createTimeoutController(timeout);
    
    try {
      const response = await fetch(url, {
        ...init,
        headers,
        signal: controller.signal,
      });

      return response;

    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Timeout: La request tardó más de ${timeout}ms`);
        }
        
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {          
          // Reintentar si quedan intentos
          if (attempt < retries) {
            const delayMs = API_CORE_CONFIG.RETRY_DELAY * Math.pow(2, attempt); // Delay exponencial
            await delay(delayMs);
            return makeRequest(attempt + 1);
          }
          
          throw new Error('Error de conexión: No se pudo conectar con el servidor');
        }
      }
      
      throw error;
    }
  };

  return makeRequest(0);
}

/**
 * Parsear respuesta JSON con manejo mejorado de errores
 */
async function parseJson<T>(resp: Response, opts?: { skipSessionAuth?: boolean }): Promise<T> {
  const raw = await resp.text();

  if (!resp.ok) {
    // Manejar errores de autenticación específicos
    // (skipSessionAuth: llamadas públicas no dependen de una sesión — no corresponde
    // redirigir a login por un 401/403, que en ese contexto es solo un error de API key)
    if ((resp.status === 401 || resp.status === 403) && !opts?.skipSessionAuth) {
      redirectToLogin();
      throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
    }
    
    // Intentar parsear mensaje de error del servidor
    let errorMessage = `Error HTTP ${resp.status}`;
    try {
      const errorData = JSON.parse(raw);
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Si no se puede parsear, usar el raw text o mensaje por defecto
      errorMessage = raw || errorMessage;
    }
    
    // Mensajes user-friendly según código de estado
    switch (resp.status) {
      case 400:
        throw new Error(`Datos inválidos: ${errorMessage}`);
      case 404:
        throw new Error('Recurso no encontrado');
      case 429:
        throw new Error('Demasiadas solicitudes. Intenta más tarde');
      case 500:
      case 502:
      case 503:
        throw new Error('Error del servidor. Intenta más tarde');
      default:
        throw new Error(errorMessage);
    }
  }
  
  // Respuesta exitosa
  if (!raw || raw.trim().length === 0) {
    return undefined as unknown as T;
  }
  
  try {
    const parsed = JSON.parse(raw);
    return parsed as T;
  } catch (parseError) {
    console.warn('⚠️ No se pudo parsear JSON, devolviendo texto plano');
    return raw as unknown as T;
  }
}

/**
 * Funciones HTTP con opciones extendidas
 */
export async function apiGet<T>(
  path: string,
  params?: QueryParams,
  options?: RequestOptions
): Promise<T> {
  const q = buildQuery(params);
  const resp = await request(`${path}${q}`, { ...options, method: 'GET' });
  return await parseJson<T>(resp, { skipSessionAuth: options?.skipSessionAuth });
}

export async function apiPost<T, B = unknown>(
  path: string,
  body?: B,
  options?: RequestOptions
): Promise<T> {
  const resp = await request(path, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined
  });
  return await parseJson<T>(resp, { skipSessionAuth: options?.skipSessionAuth });
}

export async function apiPatch<T, B = unknown>(
  path: string,
  body?: B,
  options?: RequestOptions
): Promise<T> {
  const resp = await request(path, { ...options, method: 'PATCH', body: body ? JSON.stringify(body) : undefined });
  return await parseJson<T>(resp, { skipSessionAuth: options?.skipSessionAuth });
}

export async function apiPut<T, B = unknown>(
  path: string,
  body?: B,
  options?: RequestOptions
): Promise<T> {
  const resp = await request(path, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined
  });
  return await parseJson<T>(resp, { skipSessionAuth: options?.skipSessionAuth });
}

export async function apiDelete<T>(
  path: string,
  options?: RequestOptions
): Promise<T> {
  const resp = await request(path, { ...options, method: 'DELETE' });
  return await parseJson<T>(resp, { skipSessionAuth: options?.skipSessionAuth });
}

/**
 * Cliente HTTP unificado con todas las mejoras
 */
export const http = { 
  get: apiGet, 
  post: apiPost, 
  put: apiPut, 
  delete: apiDelete,
  patch: apiPatch,
};